import { TextField, Autocomplete, Paper, Button, Typography, Box, Stack } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Loading from '../../../components/loading';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import IMask from 'imask';
import { usePatient } from '../../hooks/usePatient';
import { useSavePatient } from '../../hooks/useSavePatient';
import { useDeactivatePatient } from '../../hooks/useDeactivatePatient';
import { useCityCatalog } from '../../../hooks/useCityCatalog';
import CityFormModal from '../../../register/cities/cityFormModal';
import DeactivatePatientDialog from './deactivatePatientDialog';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';

export default function GeneralDataMobile() {
  const { patientId } = useParams();

  const navigate = useNavigate();

  const cpfMask = useRef(null);
  const responsibleCpfMask = useRef(null);
  const phoneNumberMask = useRef(null);
  const responsiblePhoneNumberMask = useRef(null);
  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [gender, setGender] = useState('');
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [street, setStreet] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [indicatedBy, setIndicatedBy] = useState('');
  const [previousDentist, setPreviousDentist] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [notes, setNotes] = useState('');
  const [firstAppointmentCredit, setFirstAppointmentCredit] = useState('0');
  const [responsibleName, setResponsibleName] = useState('');
  const [responsibleBirthDate, setResponsibleBirthDate] = useState(null);
  const [responsibleCpf, setResponsibleCpf] = useState('');
  const [responsiblePhone, setResponsiblePhone] = useState('');
  const [responsibleKinship, setResponsibleKinship] = useState('');
  const [patientStatus, setPatientStatus] = useState('');
  const [isDeactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  const { cities: citiesToComboBox, refetch: refetchCities } = useCityCatalog();
  const [isCityFormOpen, setCityFormOpen] = useState(false);
  const [cityInputValue, setCityInputValue] = useState('');
  const {
    patient: fetchedPatient,
    isLoading: isPatientLoading,
    isStale: isPatientStale,
    updatedAt: patientUpdatedAt,
  } = usePatient(patientId);
  const { save: savePatientRequest } = useSavePatient();
  const { deactivate: deactivatePatientRequest } = useDeactivatePatient();

  useEffect(() => {
    if (!fetchedPatient) {
      return;
    }
    setName(fetchedPatient.nome);
    setBirthDate(dayjs(fetchedPatient.dataDeNascimento, 'DD/MM/YYYY'));
    setGender(fetchedPatient.sexo);
    setCpf(fetchedPatient.cpf);
    setRg(fetchedPatient.rg);
    setPhoneNumber(fetchedPatient.telefone);
    setStreet(fetchedPatient.rua);
    setStreetNumber(fetchedPatient.numero);
    setNeighborhood(fetchedPatient.bairro);
    setSelectedCityId(fetchedPatient.cityCatalogId ?? '');
    setAddressComplement(fetchedPatient.complementoDeEndereco);
    setIndicatedBy(fetchedPatient.nomeDeQuemIndicou);
    setPreviousDentist(fetchedPatient.nomeDoCirurgiaoDentistaAnterior);
    setEmergencyContact(fetchedPatient.contatoDeEmergencia);
    setNotes(fetchedPatient.anotacoes);
    setFirstAppointmentCredit(fetchedPatient.creditoDaPrimeiraConsulta);
    setResponsibleName(fetchedPatient.nomeDoResponsavel);
    setResponsibleBirthDate(dayjs(fetchedPatient.dataDeNascimentoDoResponsavel, 'DD/MM/YYYY'));
    setResponsibleCpf(fetchedPatient.cpfDoResponsavel);
    setResponsiblePhone(fetchedPatient.telefoneDoResponsavel);
    setResponsibleKinship(fetchedPatient.parentescoDoResponsavel);
    setPatientStatus(fetchedPatient.status);
  }, [fetchedPatient]);

  useEffect(() => {
    applyMaskDefinition();
  }, []);

  const selectedCity = citiesToComboBox.find((city) => city.id === selectedCityId) ?? null;

  const openCityForm = () => setCityFormOpen(true);

  const closeCityForm = () => setCityFormOpen(false);

  const handleCityCreated = (savedCity) => {
    refetchCities().then((freshCities) => {
      const created = (freshCities ?? []).find(
        (city) => city.name === savedCity.name && city.state === savedCity.state,
      );
      if (created) setSelectedCityId(created.id);
    });
  };

  const savePatient = (e) => {
    e.preventDefault();

    if (!name || !name.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('O campo "Nome" é obrigatório.');
      return;
    }

    setLoading(true);

    const patient = {
      id: patientId,
      nome: name,
      cpf: cpf,
      rg: rg,
      sexo: gender,
      dataDeNascimento: birthDate,
      telefone: phoneNumber,
      rua: street,
      numero: streetNumber,
      bairro: neighborhood,
      cityCatalogId: selectedCityId || null,
      complementoDeEndereco: addressComplement,
      nomeDoResponsavel: responsibleName,
      dataDeNascimentoDoResponsavel: responsibleBirthDate,
      cpfDoResponsavel: responsibleCpf,
      telefoneDoResponsavel: responsiblePhone,
      parentescoDoResponsavel: responsibleKinship,
      nomeDeQuemIndicou: indicatedBy,
      contatoDeEmergencia: emergencyContact,
      anotacoes: notes,
      nomeDoCirurgiaoDentistaAnterior: previousDentist,
      creditoDaPrimeiraConsulta: firstAppointmentCredit,
    };

    if (patientId) {
      savePatientRequest(patient)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data);
        })
        .catch((returnFromApi) => {
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          setLoading(false);
          console.error(returnFromApi);
        });
    } else {
      savePatientRequest(patient)
        .then((returnFromApi) => {
          setLoading(false);
          setShowSuccessMessage(true);
          setSuccessMessage(returnFromApi.data.message);
          navigate(`/patient-form/${returnFromApi.data.patientId}`);
        })
        .catch((returnFromApi) => {
          setShowErrorMessage(true);
          setErrorMessage(returnFromApi.response?.data);
          setLoading(false);
          console.error(returnFromApi);
        });
    }
  };

  function applyMaskDefinition() {
    const masks = [];
    const maskToCPF = '000.000.000-00';
    const maskToPhoneNumber = '(00) 00000-0000';

    if (cpfMask.current) {
      const mask = IMask(cpfMask.current, { mask: maskToCPF });
      mask.on('accept', () => setCpf(mask.value));
      masks.push(mask);
    }

    if (responsibleCpfMask.current) {
      const mask = IMask(responsibleCpfMask.current, { mask: maskToCPF });
      mask.on('accept', () => setResponsibleCpf(mask.value));
      masks.push(mask);
    }

    if (phoneNumberMask.current) {
      const mask = IMask(phoneNumberMask.current, { mask: maskToPhoneNumber });
      mask.on('accept', () => setPhoneNumber(mask.value));
      masks.push(mask);
    }

    if (responsiblePhoneNumberMask.current) {
      const mask = IMask(responsiblePhoneNumberMask.current, { mask: maskToPhoneNumber });
      mask.on('accept', () => setResponsiblePhone(mask.value));
      masks.push(mask);
    }

    return () => masks.forEach((mask) => mask.destroy());
  }

  const handleDeactivateConfirm = () => {
    setDeactivateDialogOpen(false);
    setLoading(true);
    deactivatePatientRequest(patientId)
      .then((returnFromApi) => {
        setLoading(false);
        setPatientStatus('INACTIVE');
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Box component="form" onSubmit={savePatient}>
      <Loading isToHidden={isLoading === false && isPatientLoading === false} />
      {isPatientStale && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sem conexão com o servidor. Exibindo os últimos dados salvos do prontuário
          {patientUpdatedAt ? ` (${dayjs(patientUpdatedAt).format('DD/MM/YYYY HH:mm')})` : ''}.
          Alterações não podem ser salvas offline.
        </Alert>
      )}
      {patientStatus === 'INACTIVE' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Paciente desativado.
        </Alert>
      )}
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Stack spacing={4}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Dados do Paciente
        </Typography>

        <TextField
          data-cy="patient-name"
          fullWidth
          label="Nome"
          name="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="CPF"
          value={cpf}
          inputRef={cpfMask}
          onChange={(e) => setCpf(e.target.value)}
          fullWidth
        />
        <DatePicker
          label="Data de Nascimento"
          value={birthDate}
          onChange={(newValue) => setBirthDate(newValue)}
          sx={{ width: '100%' }}
        />

        <Autocomplete
          size="small"
          fullWidth
          options={citiesToComboBox}
          getOptionLabel={(option) => `${option.name} - ${option.state}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedCity ?? null}
          onInputChange={(_, newInputValue) => setCityInputValue(newInputValue)}
          onChange={(_, newValue) => setSelectedCityId(newValue?.id ?? '')}
          noOptionsText="Nenhuma cidade encontrada."
          PaperComponent={({ children, ...other }) => (
            <Paper {...other}>
              {children}
              <Box
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Button fullWidth size="small" onClick={openCityForm}>
                  + Nova Cidade
                </Button>
              </Box>
            </Paper>
          )}
          renderInput={(params) => (
            <TextField {...params} data-cy="city-select" label="Cidade" fullWidth />
          )}
        />

        <TextField
          fullWidth
          label="Telefone"
          name="phone"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          inputRef={phoneNumberMask}
        />
        <Button
          data-cy="save-patient-btn"
          variant="contained"
          type="submit"
          fullWidth
          sx={{ mt: 1, p: 1 }}
        >
          Salvar
        </Button>
        {patientId && patientStatus !== 'INACTIVE' && (
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => setDeactivateDialogOpen(true)}
            disabled={isPatientStale}
          >
            Desativar Paciente
          </Button>
        )}
      </Stack>

      <DeactivatePatientDialog
        open={isDeactivateDialogOpen}
        patientName={name}
        onCancel={() => setDeactivateDialogOpen(false)}
        onConfirm={handleDeactivateConfirm}
      />

      <CityFormModal
        open={isCityFormOpen}
        onClose={closeCityForm}
        onSave={handleCityCreated}
        initialName={selectedCity ? '' : cityInputValue}
      />
    </Box>
  );
}
